/* eslint-disable */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, AlertCircle } from "lucide-react";
import { propertiesApi } from "./api";
import PhotosEditor from "./PhotosEditor";
import "./CreateListing.css";

const AMENITY_CATEGORIES = [
  { key: "bedroom", label: "Bedroom", items: ["Beds", "Mattresses", "Wardrobes", "AC", "Curtains"] },
  { key: "living", label: "Living Room", items: ["Sofa", "TV", "TV Unit", "Center Table", "Curtains"] },
  { key: "kitchen", label: "Kitchen", items: ["Modular Kitchen", "Chimney", "Refrigerator", "Microwave", "Water Purifier", "Gas Stove"] },
  { key: "bathroom", label: "Bathroom", items: ["Geyser", "Exhaust Fan"] },
  { key: "other", label: "Other", items: ["Washing Machine", "Dining Table", "Study Table", "Power Backup", "Private Garden", "Terrace Access", "Servant Room", "Store Room", "Balcony"] },
];

const EMPTY = {
  bedrooms: 3,
  bathrooms: 3,
  parking: 2,
  built_up_area_sqft: "",
  facing: "",
  furnishing: "SEMI",
  description: "",
  property_age_years: "",
  floor_label: "",
  monthly_rent_inr: "",
  monthly_maintenance_inr: "",
  security_deposit_months: 2,
  available_from: "",
  min_lease_months: 11,
  expected_price_inr: "",
  possession: "READY",
  price_negotiable: false,
  vegetarian_only: false,
  pets_allowed: false,
  non_smokers_only: false,
  show_phone: true,
  allow_calls: true,
  inquiries_via_email: true,
  inquiries_via_whatsapp: true,
  visibility: "COMMUNITY_ONLY",
};

export default function EditListing() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [amenities, setAmenities] = useState({});
  const [photos, setPhotos] = useState([]);
  const [listingType, setListingType] = useState("RENT");
  const [villaNumber, setVillaNumber] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedMsg, setSavedMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await propertiesApi.get(id);
        if (cancelled) return;
        setForm({
          bedrooms: d.bedrooms,
          bathrooms: d.bathrooms,
          parking: d.parking,
          built_up_area_sqft: d.built_up_area_sqft,
          facing: d.facing || "",
          furnishing: d.furnishing,
          description: d.description || "",
          property_age_years: d.property_age_years ?? "",
          floor_label: d.floor_label || "",
          monthly_rent_inr: d.monthly_rent_inr ?? "",
          monthly_maintenance_inr: d.monthly_maintenance_inr ?? "",
          security_deposit_months: d.security_deposit_months ?? 2,
          available_from: d.available_from || "",
          min_lease_months: d.min_lease_months ?? 11,
          expected_price_inr: d.expected_price_inr ?? "",
          possession: d.possession || "READY",
          price_negotiable: !!d.price_negotiable,
          vegetarian_only: !!d.vegetarian_only,
          pets_allowed: !!d.pets_allowed,
          non_smokers_only: !!d.non_smokers_only,
          show_phone: !!d.show_phone,
          allow_calls: !!d.allow_calls,
          inquiries_via_email: !!d.inquiries_via_email,
          inquiries_via_whatsapp: !!d.inquiries_via_whatsapp,
          visibility: d.visibility,
        });
        setAmenities(d.amenities || {});
        setPhotos(d.photos || []);
        setListingType(d.type);
        setVillaNumber(d.villa?.villa_number || "");
        if (!d.can_edit) {
          setError("You don't have permission to edit this listing.");
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Could not load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleAmenity = (cat, label) => {
    setAmenities((m) => {
      const items = m[cat] || [];
      const has = items.includes(label);
      return {
        ...m,
        [cat]: has ? items.filter((x) => x !== label) : [...items, label],
      };
    });
  };

  const save = async () => {
    setError("");
    setSavedMsg("");
    setSaving(true);
    try {
      const body = {
        bedrooms: Number(form.bedrooms),
        bathrooms: Number(form.bathrooms),
        parking: Number(form.parking || 0),
        built_up_area_sqft: Number(form.built_up_area_sqft),
        facing: form.facing || null,
        furnishing: form.furnishing,
        description: form.description.trim(),
        property_age_years: form.property_age_years
          ? Number(form.property_age_years)
          : null,
        floor_label: form.floor_label || null,
        monthly_rent_inr: form.monthly_rent_inr
          ? Number(form.monthly_rent_inr)
          : null,
        monthly_maintenance_inr: form.monthly_maintenance_inr
          ? Number(form.monthly_maintenance_inr)
          : null,
        security_deposit_months: form.security_deposit_months
          ? Number(form.security_deposit_months)
          : null,
        available_from: form.available_from || null,
        min_lease_months: form.min_lease_months
          ? Number(form.min_lease_months)
          : null,
        expected_price_inr: form.expected_price_inr
          ? Number(form.expected_price_inr)
          : null,
        possession: form.possession || null,
        price_negotiable: !!form.price_negotiable,
        vegetarian_only: !!form.vegetarian_only,
        pets_allowed: !!form.pets_allowed,
        non_smokers_only: !!form.non_smokers_only,
        show_phone: !!form.show_phone,
        allow_calls: !!form.allow_calls,
        inquiries_via_email: !!form.inquiries_via_email,
        inquiries_via_whatsapp: !!form.inquiries_via_whatsapp,
        visibility: form.visibility,
        amenities,
      };
      await propertiesApi.patch(id, body);
      setSavedMsg("Changes saved.");
      setTimeout(() => setSavedMsg(""), 3000);
    } catch (err) {
      setError(err.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="vsp-cl-shell">
        <div className="vsp-cl-empty">Loading…</div>
      </div>
    );
  }

  return (
    <div className="vsp-cl-shell">
      <button className="vsp-cl-back" onClick={() => navigate(`/properties/${id}`)}>
        <ArrowLeft size={14} /> Back to listing
      </button>

      <h1>Edit listing</h1>
      <p className="vsp-cl-sub">
        Editing <strong>{villaNumber}</strong> — changes go live as soon as
        you save. The 30-day expiry clock isn't reset by edits.
      </p>

      <div className="vsp-cl-card">
        <h2 className="vsp-cl-section">Basic details</h2>
        <BasicFields form={form} upd={upd} />

        <h2 className="vsp-cl-section">Photos</h2>
        <PhotosEditor listingId={id} initial={photos} onChange={setPhotos} />

        <h2 className="vsp-cl-section">Amenities</h2>
        <AmenityGrid amenities={amenities} onToggle={toggleAmenity} />

        <h2 className="vsp-cl-section">
          {listingType === "RENT" ? "Rent details" : "Sale details"}
        </h2>
        {listingType === "RENT" ? (
          <RentFields form={form} upd={upd} />
        ) : (
          <SaleFields form={form} upd={upd} />
        )}

        <h2 className="vsp-cl-section">Preferences</h2>
        <Preferences form={form} upd={upd} />

        <h2 className="vsp-cl-section">Visibility</h2>
        <VisibilityPicker form={form} upd={upd} />

        {error && (
          <div className="vsp-cl-error">
            <AlertCircle size={14} /> {error}
          </div>
        )}
        {savedMsg && <div className="vsp-cl-saved">{savedMsg}</div>}

        <div className="vsp-cl-actions">
          <button
            type="button"
            className="vsp-cl-btn-secondary"
            onClick={() => navigate(`/properties/${id}`)}
          >
            Cancel
          </button>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="vsp-cl-btn-primary"
            onClick={save}
            disabled={saving}
          >
            <Save size={14} /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BasicFields({ form, upd }) {
  return (
    <div className="vsp-cl-grid">
      <Field label="Furnishing">
        <div className="vsp-cl-pills">
          {[
            ["FURNISHED", "Furnished"],
            ["SEMI", "Semi"],
            ["UNFURNISHED", "Unfurnished"],
          ].map(([v, label]) => (
            <button
              key={v}
              type="button"
              className={`vsp-cl-pill ${form.furnishing === v ? "active" : ""}`}
              onClick={() => upd({ furnishing: v })}
            >
              {label}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Bedrooms">
        <div className="vsp-cl-pills">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              className={`vsp-cl-pill ${Number(form.bedrooms) === n ? "active" : ""}`}
              onClick={() => upd({ bedrooms: n })}
            >
              {n === 5 ? "5+" : n}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Bathrooms">
        <input
          type="number"
          min={1}
          max={20}
          value={form.bathrooms}
          onChange={(e) => upd({ bathrooms: e.target.value })}
        />
      </Field>
      <Field label="Parking">
        <input
          type="number"
          min={0}
          max={20}
          value={form.parking}
          onChange={(e) => upd({ parking: e.target.value })}
        />
      </Field>
      <Field label="Built-up area (sqft)">
        <input
          type="number"
          min={100}
          value={form.built_up_area_sqft}
          onChange={(e) => upd({ built_up_area_sqft: e.target.value })}
        />
      </Field>
      <Field label="Facing">
        <select value={form.facing} onChange={(e) => upd({ facing: e.target.value })}>
          <option value="">— optional —</option>
          <option value="EAST">East</option>
          <option value="WEST">West</option>
          <option value="NORTH">North</option>
          <option value="SOUTH">South</option>
        </select>
      </Field>
      <Field label="Floor">
        <input
          value={form.floor_label}
          onChange={(e) => upd({ floor_label: e.target.value })}
          placeholder="e.g. Ground + 1"
        />
      </Field>
      <Field label="Property age (years)">
        <input
          type="number"
          min={0}
          value={form.property_age_years}
          onChange={(e) => upd({ property_age_years: e.target.value })}
        />
      </Field>
      <Field label="Description" wide>
        <textarea
          rows={5}
          value={form.description}
          onChange={(e) => upd({ description: e.target.value })}
          maxLength={2000}
        />
        <p className="vsp-cl-helper">{form.description.length} / 2000</p>
      </Field>
    </div>
  );
}

function RentFields({ form, upd }) {
  return (
    <div className="vsp-cl-grid">
      <Field label="Monthly rent (₹)">
        <input type="number" min={0} value={form.monthly_rent_inr} onChange={(e) => upd({ monthly_rent_inr: e.target.value })} />
      </Field>
      <Field label="Monthly maintenance (₹)">
        <input type="number" min={0} value={form.monthly_maintenance_inr} onChange={(e) => upd({ monthly_maintenance_inr: e.target.value })} />
      </Field>
      <Field label="Security deposit (months)">
        <input type="number" min={0} max={12} value={form.security_deposit_months} onChange={(e) => upd({ security_deposit_months: e.target.value })} />
      </Field>
      <Field label="Available from">
        <input type="date" value={form.available_from} onChange={(e) => upd({ available_from: e.target.value })} />
      </Field>
      <Field label="Minimum lease (months)">
        <select value={form.min_lease_months} onChange={(e) => upd({ min_lease_months: e.target.value })}>
          <option value={6}>6 months</option>
          <option value={11}>11 months</option>
          <option value={12}>1 year</option>
          <option value={24}>2 years</option>
        </select>
      </Field>
    </div>
  );
}

function SaleFields({ form, upd }) {
  return (
    <div className="vsp-cl-grid">
      <Field label="Expected price (₹)">
        <input type="number" min={0} value={form.expected_price_inr} onChange={(e) => upd({ expected_price_inr: e.target.value })} />
      </Field>
      <Field label="Possession">
        <select value={form.possession} onChange={(e) => upd({ possession: e.target.value })}>
          <option value="READY">Ready to Move</option>
          <option value="WITHIN_3_MONTHS">Within 3 months</option>
          <option value="WITHIN_6_MONTHS">Within 6 months</option>
        </select>
      </Field>
      <Field label="Negotiable?">
        <Toggle checked={form.price_negotiable} onChange={(v) => upd({ price_negotiable: v })} label="Price is negotiable" />
      </Field>
    </div>
  );
}

function Preferences({ form, upd }) {
  return (
    <div className="vsp-cl-pref">
      <Toggle checked={form.vegetarian_only} onChange={(v) => upd({ vegetarian_only: v })} label="Vegetarians only" />
      <Toggle checked={form.pets_allowed} onChange={(v) => upd({ pets_allowed: v })} label="Pets allowed" />
      <Toggle checked={form.non_smokers_only} onChange={(v) => upd({ non_smokers_only: v })} label="Non-smokers only" />
    </div>
  );
}

function VisibilityPicker({ form, upd }) {
  return (
    <div className="vsp-cl-pills">
      <button type="button" className={`vsp-cl-pill ${form.visibility === "COMMUNITY_ONLY" ? "active" : ""}`} onClick={() => upd({ visibility: "COMMUNITY_ONLY" })}>
        Community only
      </button>
      <button type="button" className={`vsp-cl-pill ${form.visibility === "PUBLIC" ? "active" : ""}`} onClick={() => upd({ visibility: "PUBLIC" })}>
        Public
      </button>
      <button type="button" className={`vsp-cl-pill ${form.visibility === "HIDDEN" ? "active" : ""}`} onClick={() => upd({ visibility: "HIDDEN" })}>
        Hidden (draft)
      </button>
    </div>
  );
}

function AmenityGrid({ amenities, onToggle }) {
  return (
    <div className="vsp-cl-amen">
      {AMENITY_CATEGORIES.map((cat) => (
        <div key={cat.key} className="vsp-cl-amen-cat">
          <h4>{cat.label}</h4>
          <div className="vsp-cl-amen-items">
            {cat.items.map((item) => {
              const checked = (amenities[cat.key] || []).includes(item);
              return (
                <label key={item} className={`vsp-cl-amen-item ${checked ? "checked" : ""}`}>
                  <input type="checkbox" checked={checked} onChange={() => onToggle(cat.key, item)} />
                  <span>{item}</span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function Field({ label, children, wide }) {
  return (
    <div className={`vsp-cl-field ${wide ? "wide" : ""}`}>
      <label>{label}</label>
      {children}
    </div>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <label className="vsp-cl-toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}
